import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private deferred?: BeforeInstallPromptEvent;

  readonly canInstall$ = new BehaviorSubject<boolean>(false);
  readonly installed$ = new BehaviorSubject<boolean>(false);

  constructor(@Inject(PLATFORM_ID) platformId: Object, @Inject(DOCUMENT) private doc: Document) {
    if (!isPlatformBrowser(platformId)) return;

    const isStandalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      // older iOS:
      (navigator as any).standalone === true;

    this.installed$.next(isStandalone);

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferred = e as BeforeInstallPromptEvent;
      this.canInstall$.next(true);
    });

    window.addEventListener('appinstalled', () => {
      this.deferred = undefined;
      this.canInstall$.next(false);
      this.installed$.next(true);
    });
  }

  async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const ev = this.deferred;
    if (!ev) return 'unavailable';
    this.canInstall$.next(false);
    await ev.prompt();
    const { outcome } = await ev.userChoice;
    this.deferred = undefined;
    return outcome;
  }
}
